// TeamMembersSurface.tsx — /people, the team roster.
//
// Redesign port 2026-07 (prototype people-page.tsx): roster ROWS (avatar · name+Admin chip ·
// role · joined · per-member source chips from tools_used) with an inline Invite panel and an
// admin-only Pending panel with revoke. **The invite FORM is visible to every member, but only
// an ADMIN can actually send** (bug-551 — POST /api/teams/[id]/invite is admin-gated by
// deliberate policy, Teddy 2026-07-28; the port's old "D15 — invite open to all" note is
// superseded). A non-admin's Send therefore 403s, and inviteErrorText names that reason rather
// than offering a retry that cannot work. Do not "fix" this by widening the route. The
// prototype's no-op affordances stay REAL here: clicking a row opens the prod drawers — your own
// card opens the self-edit drawer (name / role / visibility tier via the live useUpdateProfile);
// a teammate's opens the read-only profile, where admins additionally get role/remove. No new
// backend — every write reuses a hook that already shipped (useUpdateProfile / useSendInvite /
// useRevokeInvite / useRemoveMember).

import { useState, useEffect, type FormEvent } from 'react';
import { MoreHorizontal, Send } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { Drawer } from '../shared/Drawer';
import type { TeamMembersSurfaceData, TeamMember } from '../../hooks/useTeamMembersSurface';
import type { TeamSettingsInvite } from '../../hooks/useTeamSettings';
import { useUpdateProfile, useSendInvite, useRevokeInvite, useRemoveMember } from '../../hooks/useTeamSettings';
import { VISIBILITY_TIERS, tierLabel, DEFAULT_VISIBILITY_TIER } from '../../lib/visibility-tiers';
import { relativeTime } from '../../lib/formatting';
import { siteLabel } from '../../lib/sites';
import { PageMast, MastTile } from '../shared/workroom';

interface TeamMembersSurfaceProps {
  data: TeamMembersSurfaceData;
  // Passed by the route for parity with other surfaces; People navigates via drawers, not nav.
  onNav?: (id: string) => void;
  // The signed-in user's id (so the roster can identify + badge "You" and self-edit).
  currentUserId?: string;
  // Invite + admin affordances — teamId/admin/pending come from the team-settings payload.
  teamId?: string;
  teamName?: string;
  isAdmin?: boolean;
  pendingInvites?: TeamSettingsInvite[];
}

// Deterministic avatar tone from a stable seed (user_id), so a member keeps the same color.
// (Per-surface duplication is the documented convention — live-shared carries its own copy.)
const AV_COLORS = ['blue', 'sage', 'sand', 'lilac', 'clay', 'teal'] as const;
const AV_TONES: Record<string, { bg: string; ink: string }> = {
  blue: { bg: '#dce8ec', ink: '#527c90' },
  sage: { bg: '#e4eadf', ink: '#62725c' },
  sand: { bg: '#eee5d5', ink: '#887157' },
  lilac: { bg: '#e8e2f0', ink: '#4a3d6b' },
  clay: { bg: '#f5d8ce', ink: '#b95c4b' },
  teal: { bg: '#f1e3ce', ink: '#8b6546' },
};
function avTone(seed: string): { bg: string; ink: string } {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (Math.imul(h, 31) + seed.charCodeAt(i)) >>> 0;
  return AV_TONES[AV_COLORS[h % AV_COLORS.length]];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function RoundAvatar({ member, you }: { member: TeamMember; you: boolean }) {
  if (you) {
    return <span className="grid size-10 shrink-0 place-items-center rounded-full bg-acc-soft text-[15px] font-semibold text-acc">{initials(member.display_name)}</span>;
  }
  const tone = avTone(member.user_id);
  return (
    <span className="grid size-10 shrink-0 place-items-center rounded-full text-[15px] font-semibold" style={{ backgroundColor: tone.bg, color: tone.ink }}>
      {initials(member.display_name)}
    </span>
  );
}

// "Working on" chips: prefer current_focus (live), fall back to recent_topics. Both may be
// null/absent at runtime — fully null-safe, renders nothing when there's no signal.
function workingTags(m: TeamMember): string[] {
  const focus = m.current_focus && m.current_focus.length ? m.current_focus : m.recent_topics;
  return (focus ?? []).slice(0, 4);
}

// "joined March" — month name, with the year appended once it's not the current year.
function joinedLabel(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const year = d.getFullYear();
  return `joined ${month}${year !== new Date().getFullYear() ? ` ${year}` : ''}`;
}

const WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];
const countWord = (n: number) => WORDS[n] ?? String(n);

const FEED_OK = 'rounded-lg border border-[#b9cbaf] bg-[#eef3e9] px-3 py-2 text-[12px] leading-5 text-[#4f6d47]';
const FEED_ERR = 'rounded-lg border border-[#e2aa9c] bg-[#fdf0e7] px-3 py-2 text-[12px] leading-5 text-[#8c5040]';

function MemberRow({ member, you, onOpen }: { member: TeamMember; you: boolean; onOpen: () => void }) {
  const joined = joinedLabel(member.joined_at);
  const sources = (member.tools_used ?? []).slice(0, 6);
  return (
    <div
      className="flex cursor-pointer items-center gap-3 border-b border-border py-3.5 last:border-b-0"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <RoundAvatar member={member} you={you} />
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-2 text-[15px] font-medium leading-5">
          <span className="truncate">{member.display_name}</span>
          {you && <span className="shrink-0 rounded-full bg-acc-soft px-2 py-0.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.12em] text-acc">You</span>}
          {member.role === 'admin' && <span className="shrink-0 rounded-full bg-acc-soft px-2 py-0.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.12em] text-acc">Admin</span>}
        </p>
        <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
          {member.display_role}{joined ? ` · ${joined}` : ''}
        </p>
      </div>
      <div className="hidden flex-wrap justify-end gap-1.5 sm:flex">
        {sources.map((s) => (
          <span key={s} className="rounded-full border border-border px-2 py-0.5 font-['DM_Mono'] text-[8px] uppercase tracking-[.1em] text-muted-foreground">
            {siteLabel(s) ?? s}
          </span>
        ))}
      </div>
      <span className="grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground">
        <MoreHorizontal size={16} />
      </span>
    </div>
  );
}

// d-you — the only editable card. Edits name / role / visibility tier via the existing
// self-gated PATCH (useUpdateProfile). D9 copy makes "admin ≠ surveillance" explicit.
function YouDrawer({ open, member, teamId, onClose }: {
  open: boolean; member: TeamMember; teamId?: string; onClose: () => void;
}) {
  const update = useUpdateProfile();
  const baseTier = member.visibility_tier || DEFAULT_VISIBILITY_TIER;
  const [name, setName] = useState(member.display_name);
  const [role, setRole] = useState(member.display_role);
  const [tier, setTier] = useState(baseTier);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the live member each time the drawer opens (or the member data changes).
  useEffect(() => {
    if (!open) return;
    setName(member.display_name);
    setRole(member.display_role);
    setTier(member.visibility_tier || DEFAULT_VISIBILITY_TIER);
    setError(null);
  }, [open, member.display_name, member.display_role, member.visibility_tier]);

  const dirty = name !== member.display_name || role !== member.display_role || tier !== baseTier;

  const save = () => {
    if (!teamId || !dirty) return;
    const updates: { display_name?: string; display_role?: string; visibility_tier?: string } = {};
    if (name !== member.display_name) updates.display_name = name.trim();
    if (role !== member.display_role) updates.display_role = role.trim();
    if (tier !== baseTier) updates.visibility_tier = tier;
    update.mutate({ teamId, userId: member.user_id, updates }, {
      onSuccess: onClose,
      onError: () => setError('Couldn’t save your profile. Try again.'),
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`You · ${member.display_name}`}
      subtitle="What your team sees of you"
    >
      <div className="dgroup">
        <div className="dlabel">Display name</div>
        <input className="di" value={name} maxLength={50} onChange={e => setName(e.target.value)} />
      </div>
      <div className="dgroup">
        <div className="dlabel">Role</div>
        <input className="di" value={role} maxLength={100} onChange={e => setRole(e.target.value)} />
      </div>
      <div className="dgroup">
        <div className="dlabel">What you share with the team</div>
        {VISIBILITY_TIERS.map(t => (
          <div key={t} className="drow" style={{ cursor: 'pointer' }} onClick={() => setTier(t)}>
            <span className="dk">{tierLabel(t)}</span>
            <span className="dv">{tier === t ? <><span className="dot acc" /> selected</> : null}</span>
          </div>
        ))}
        <div className="dsub">
          Admins manage the team, not what they can see - your visibility is governed only by your
          tier and per-conversation private flags, equally for everyone.
        </div>
      </div>
      {error && <div className={FEED_ERR}>{error}</div>}
      <button className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={!dirty || update.isPending} onClick={save}>
        {update.isPending ? 'Saving…' : <>Save <span className="arr">→</span></>}
      </button>
    </Drawer>
  );
}

// d-member — read-only teammate profile. Admins (and never on their own card) get a role
// dropdown + a Remove confirm; non-admins see a one-line note instead of a "no access" wall (D8).
function MemberDrawer({ member, isAdmin, teamId, currentUserId, onClose }: {
  member: TeamMember | null; isAdmin: boolean; teamId?: string; currentUserId?: string; onClose: () => void;
}) {
  const update = useUpdateProfile();
  const remove = useRemoveMember();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setConfirming(false); setError(null); }, [member?.user_id]);

  const tags = member ? workingTags(member) : [];
  const canManage = isAdmin && !!member && member.user_id !== currentUserId;

  const onRole = (role: string) => {
    if (!teamId || !member) return;
    update.mutate({ teamId, userId: member.user_id, updates: { role } }, {
      onError: () => setError('Couldn’t change the role. Try again.'),
    });
  };
  const onRemove = () => {
    if (!teamId || !member) return;
    remove.mutate({ teamId, userId: member.user_id }, {
      onSuccess: onClose,
      onError: () => setError('Couldn’t remove this member. Try again.'),
    });
  };

  const lastActive = member?.last_active ? relativeTime(member.last_active) : null;

  return (
    <Drawer
      open={!!member}
      onClose={onClose}
      title={member?.display_name ?? ''}
      subtitle={member ? `${member.display_role}${lastActive ? ` · ${lastActive}` : ''}` : ''}
    >
      {member && (
        <>
          {tags.length > 0 && (
            <div className="dgroup">
              <div className="dlabel">Working on</div>
              <div className="ptags">{tags.map(t => <span key={t} className="lab">{t}</span>)}</div>
            </div>
          )}
          {member.skills && member.skills.length > 0 && (
            <div className="dgroup">
              <div className="dlabel">Skills</div>
              <div className="ptags">{member.skills.map(s => <span key={s.name} className="lab">{s.name}</span>)}</div>
            </div>
          )}
          <div className="dgroup">
            <div className="dlabel">Shares with team</div>
            <div className="drow">
              <span className="dk">Visibility</span>
              <span className="dv"><span className="mpill"><span className="dot acc" />{tierLabel(member.visibility_tier)}</span></span>
            </div>
          </div>

          {canManage ? (
            <>
              <div className="dgroup">
                <div className="dlabel">Admin · manage member</div>
                <div className="drow">
                  <span className="dk">Role</span>
                  <span className="dv">
                    <select
                      className="di"
                      style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }}
                      value={member.role}
                      disabled={update.isPending}
                      onChange={e => onRole(e.target.value)}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </span>
                </div>
              </div>
              <div className="danger-zone">
                <div className="drow" style={{ border: 'none', padding: 0 }}>
                  <span className="dk" style={{ color: 'var(--rose)' }}>Remove from team</span>
                  <span className="dv">
                    {confirming ? (
                      <>
                        <button className="btn btn-secondary sm" style={{ color: 'var(--rose)' }} disabled={remove.isPending} onClick={onRemove}>
                          {remove.isPending ? 'Removing…' : 'Confirm'}
                        </button>
                        <button className="btn btn-secondary sm" disabled={remove.isPending} onClick={() => setConfirming(false)}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn btn-secondary sm" style={{ color: 'var(--rose)' }} onClick={() => setConfirming(true)}>Remove</button>
                    )}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="dsub">Only admins can change roles or remove members. Admins manage the team - they don’t see more of your work than anyone else.</div>
          )}
          {error && <div className={FEED_ERR}>{error}</div>}
        </>
      )}
    </Drawer>
  );
}

/* Say what actually happened. bug-551: every failure used to read "Try again", which was a
 * lie for the two states retrying can't fix — a role the caller doesn't have, and a rate cap
 * that resets tomorrow. Branch on ApiError.status, not on substrings of the message. */
export function inviteErrorText(error: unknown): string {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 409) return 'Already a member or invite pending for this email.';
  if (status === 403) return 'Only admins can invite on this team.';
  if (status === 429) return 'Invite limit reached for today. Try again tomorrow.';
  if (status === 500) return 'The invite email didn’t go out. Try again in a moment.';
  return 'Couldn’t send the invite. Try again.';
}

export function TeamMembersSurface({ data, currentUserId, teamId, isAdmin = false, pendingInvites = [] }: TeamMembersSurfaceProps) {
  const [youOpen, setYouOpen] = useState(false);
  const [memberPending, setMemberPending] = useState<string | null>(null);

  const send = useSendInvite();
  const revoke = useRevokeInvite();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const isYou = (m: TeamMember) => !!currentUserId && m.user_id === currentUserId;
  const youMember = data.members.find(isYou) ?? null;
  const memberMember = memberPending ? data.members.find(m => m.user_id === memberPending) ?? null : null;
  // The form shows for anyone on a team; sending is admin-only server-side (bug-551). Keeping it
  // visible is deliberate — a member learns WHY from the 403 line instead of finding no control.
  const canInvite = !!teamId;

  // Your own row leads the roster.
  const roster = [...data.members].sort((a, b) => Number(isYou(b)) - Number(isYou(a)));
  const admins = data.members.filter(m => m.role === 'admin').length;
  const plain = data.members.length - admins;
  const latestInvite = pendingInvites.length > 0 ? pendingInvites[pendingInvites.length - 1] : null;

  const onSend = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !teamId) return;
    send.mutate({ teamId, email: trimmed }, {
      onSuccess: () => { setSent(trimmed); setEmail(''); },
    });
  };

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <PageMast
          eyebrow="People · the team"
          title={<>{countWord(data.members.length)} of <em>you.</em></>}
          intro="Who’s on the team, what each person has connected, and who’s still on the way in. Sources stay personal - connecting is always each person’s own call."
          tiles={<>
            <MastTile
              label="TEAM"
              value={data.members.length}
              note={`${admins} admin${admins === 1 ? '' : 's'} · ${plain} member${plain === 1 ? '' : 's'}`}
            />
            {/* Pending invites are admin-visible data; a non-admin tile must not claim "0 pending". */}
            {isAdmin ? (
              <MastTile
                label="PENDING"
                value={pendingInvites.length}
                note={latestInvite ? `invited ${relativeTime(latestInvite.created_at)}` : 'no open invites'}
              />
            ) : (
              <MastTile label="PENDING" value="-" note="invites are managed by your admin" />
            )}
          </>}
        />

        {/* minmax(0,·) tracks: a bare-fr arbitrary track floors at the widest child's
            min-content (bug-720 class), which a long role/name line drags past the viewport. */}
        <section className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.18fr)_minmax(0,.82fr)]">
          <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
              Members · {data.members.length}
            </p>
            <div className="mt-1">
              {roster.map(member => (
                <MemberRow
                  key={member.user_id}
                  member={member}
                  you={isYou(member)}
                  onOpen={() => isYou(member) ? setYouOpen(true) : setMemberPending(member.user_id)}
                />
              ))}
            </div>
          </section>

          <div className="space-y-5">
            {canInvite && (
              <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Invite</p>
                <h2 className="mt-1 text-lg font-semibold">Bring someone in</h2>
                <form className="mt-3 flex items-center gap-2 rounded-full border border-border bg-input-background p-1.5 pl-4" onSubmit={onSend}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="teammate@company.com"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                  <button
                    type="submit"
                    disabled={send.isPending || !email.trim() || !teamId}
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-3.5 py-1.5 text-sm text-card transition hover:opacity-90 disabled:cursor-default disabled:opacity-50"
                  >
                    {send.isPending ? 'Sending…' : <>Send <Send size={13} /></>}
                  </button>
                </form>
                {sent && <div className={`mt-3 ${FEED_OK}`}>Invite sent to {sent}</div>}
                {send.isError && (
                  <div className={`mt-3 ${FEED_ERR}`}>{inviteErrorText(send.error)}</div>
                )}
                <p className="mt-3 text-[13px] leading-5 text-muted-foreground">
                  They join with nothing connected - Terum only ever hears what they choose to plug in.
                </p>
              </section>
            )}

            {isAdmin && (
              <section className="rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
                  Pending · {pendingInvites.length}
                </p>
                <div className="mt-1">
                  {pendingInvites.length === 0 && <p className="py-3 text-sm text-muted-foreground">No open invites.</p>}
                  {pendingInvites.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-3 border-b border-border py-3 last:border-b-0">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-medium leading-5">{inv.email}</p>
                        <p className="mt-0.5 text-[12px] text-muted-foreground">invited {relativeTime(inv.created_at)}</p>
                      </div>
                      {confirmRevoke === inv.id ? (
                        <span className="flex shrink-0 items-center gap-1.5">
                          <button
                            className="rounded-full border border-[#e2aa9c] bg-[#fdf0e7] px-3 py-1 text-sm text-[#8c5040] transition hover:border-[#b95c4b]"
                            disabled={revoke.isPending || !teamId}
                            onClick={() => teamId && revoke.mutate({ teamId, inviteId: inv.id }, { onSuccess: () => setConfirmRevoke(null) })}
                          >
                            {revoke.isPending ? '…' : 'Confirm'}
                          </button>
                          <button
                            className="rounded-full border border-border bg-card px-3 py-1 text-sm transition hover:border-foreground"
                            onClick={() => setConfirmRevoke(null)}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          className="shrink-0 rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3 py-1 text-sm transition hover:border-foreground hover:bg-foreground hover:text-card dark:border-border dark:bg-card"
                          onClick={() => setConfirmRevoke(inv.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </section>
      </div>

      {youMember && (
        <YouDrawer open={youOpen} member={youMember} teamId={teamId} onClose={() => setYouOpen(false)} />
      )}
      <MemberDrawer
        member={memberMember}
        isAdmin={isAdmin}
        teamId={teamId}
        currentUserId={currentUserId}
        onClose={() => setMemberPending(null)}
      />
    </div>
  );
}
